/* eslint-disable multiline-ternary */
/* eslint-disable no-unused-vars */
import React, {
  useMemo,
  useImperativeHandle,
  useCallback
} from "react";
import "./index.less";
import { Form, FormInstance, FormItemProps, FormProps } from "@tinper/next-ui";
import DataForm from "../../DataForm/src";

import type { SubmitterProps } from "./Submitter";
import CollpaseLine from "./CollpaseLine";
import { SearchFormContext } from "./SearchFormContext";
// import Selected from "./Selected";
import getNid from "../../../utils/getNid";

/**
 * 支持的查询表单项类型
 * @type 'select'[下拉框] | 'input'[输入框] | 'datePicker'[日期选择] | 'dateRangePicker'[日期范围] | 'custom'[自定义] | string
 */
declare const InputTypes: [
  "select",
  "input",
  "datePicker",
  "dateRangePicker",
  "custom",
  string
];
export declare type InputType = (typeof InputTypes)[number];

/**
 * 查询表单项入参
 * @type inputType[表单项类型]: inputType
 */
export interface FormItemType {
  inputType: InputType;
  placeholder?: string | string[];
  valuePropName?: string;
  required?: boolean;
  colStyle?: React.CSSProperties;
  isInputPressEnterCallSearch?: boolean;
  rule: any[];
  customComponent?: React.ReactNode;
  componentProps?: any;
}

export type CommonFormProps<
  T = Record<string, any>,
  U = Record<string, any>
> = {
  // 自定义样式
  style?: React.CSSProperties;
  // 自定义类名
  className?: string;
};
/**
 * 查询表单组件入参
 * @type
 */

export type SearchFormProps<
  T = Record<string, any>,
  U = Record<string, any>
> = {
  /**
   * @name 查询表单类型
   * @description 查询表单类型，simple 为简单查询，advance 为支持高级配置的查询
   */
  searchType?: "simple" | "advance";
  /**
   * @name 自定义提交的配置
   *
   * @example 不展示提交按钮和重置按钮
   * submitter={false}
   * @example 修改重置按钮的样式，并且隐藏提交按钮
   * submitter={{resetButtonProps: { type: 'dashed'},submitButtonProps: { style: { display: 'none', }}}}
   *
   * @example 修改提交按钮和重置按钮的顺序
   * submitter={{ render:(props,dom)=> [...dom.reverse()]}}
   *
   * @example 修改提交和重置按钮文字
   * submitter={{ searchConfig: { submitText: '提交2',restText: '重置2'}}}
   */
  submitter?:
    | SubmitterProps<{
        form?: FormInstance<any>;
      }>
    | false;
  /**
   * @name 重置表单的方法
   * @description 重置表单的方法，会重置表单的值
   */
  onReset?: (values: T, form: FormInstance<any>) => void;
  /**
   * @name 提交表单的方法
   * @description 提交表单的方法，会触发表单的校验
   */
  onSubmit?: (values: T, errors: U, form: FormInstance<any>) => void;
  /**
   * @name 查询表单的方法
   * @description 查询表单的方法，会触发表单的校验
   */
  onSearch?: (values: T, errors: U, form: FormInstance<any>) => void;
  contentRender?: (
    items: React.ReactNode,
    submitter: React.ReactElement<SubmitterProps> | undefined
  ) => React.ReactNode;
} & FormProps &
  CommonFormProps<T, U>;

const SearchForm: React.FC<SearchFormProps> = (
  props: SearchFormProps,
  ref = {} as any
) => {
  const nidProps = getNid(props);
  const {
    submitter,
    form,
    searchType,
    onCollapse,
    collapsedLinesNumber = 1,
    onCollapsed,
    defaultCollapsed = true,
    fieldid,
    ...restProps
  } = props;
  const [formInstance] = Form.useForm();
  const formRef = React.useRef<FormInstance<any>>(form || formInstance);
  const [collapsed, setCollapsedState] =
    React.useState<boolean>(defaultCollapsed);
  const [responCol, setResponCol] = React.useState<number>(0);
  const [showCollapsed, setShowCollapsed] = React.useState<boolean>(true);
  const [collapsedNailed, setCollapsedNailed] = React.useState<boolean>(false);

  const extendRef = useMemo(() => {
    return {
      ...ref?.current,
      ...formRef.current,
      getFieldsLogicValue: () => {
        const resVal: any[] = [];
        const values = formRef.current.getFieldsValue();
        Object.keys(values).forEach((key) => {
          const props =
            formRef.current.getFieldInstance(key)?.props?.children?.props;
          return resVal.push({
            name: key,
            value: values[key],
            // TODO默认是第一项，将来要支持选择
            compareLogic: Array.isArray(props?.compareLogic)
              ? props?.compareLogic[0]
              : props?.compareLogic
          } as any);
        });
        return resVal;
      },
      getFieldLogicValue: (name: string) => {
        if (!name) return null;
        const value = formRef.current.getFieldValue(name);
        const props =
          formRef.current.getFieldInstance(name)?.props?.children?.props;
        return {
          name,
          value,
          // TODO默认是第一项，将来要支持选择
          compareLogic: Array.isArray(props?.compareLogic)
            ? props?.compareLogic[0]
            : props?.compareLogic
        } as any;
      },
      onSubmit: () => {
        console.log("instance onSubmit");
        props?.onSubmit();
      },
      onSearch: () => {
        console.log("instance onSearch");
        props?.onSearch();
      },
      onReset: () => {
        console.log("instance onReset");
        props?.onReset();
      },
      getCollapsedConfig: () => {
        return {
          collapsed,
          collapsedLinesNumber,
          responCol,
          showCollapsed,
          collapsedNailed
        };
      },
      onCollapsed,
      setCollapsed: (...args) => {
        if (args.length === 0) return;
        setCollapsedState((val) => {
          let newVal = args[0];
          if (args[0] instanceof Function) {
            newVal = args[0].call(null, val);
          }
          onCollapsed && onCollapsed(newVal);
          return newVal;
        });
      },
      toogleCollapsed: () => {
        setCollapsedState((val: boolean) => {
          onCollapsed && onCollapsed(!val);
          return !val;
        });
      },
      setShowCollapsed,
      setCollapsedNailed
    };
  }, [ref, formRef.current, form]);

  // 暴漏给外部ref的方法
  useImperativeHandle(
    ref,
    () => {
      return extendRef;
    },
    [ref, formRef.current]
  );

  /** 计算 props 的对象 */
  const submitterProps = useMemo(() => {
    if (submitter === false || searchType === "simple") return false;
    return typeof submitter === "boolean" || !submitter ? {} : submitter;
  }, [submitter]);

  const responColState = useMemo(() => {
    return responCol;
  }, [responCol]);

  const contentRender = useCallback(
    (inputitems: any, submitter: any) => {
      if (responColState === 0) return null;
      const items = React.cloneElement(inputitems, {
        children: React.Children.map(inputitems.props.children, (item, index) => {
          if (collapsed && index + 1 > responColState * collapsedLinesNumber) {
            return React.cloneElement(item, { style: { display: "none" } });
          }
          return item;
        })
      })
      if (searchType === "simple") {
        return <>{items}</>;
      } else {
        return props.contentRender ? (
          props.contentRender(items, submitter)
        ) : (
          <>
            <div
              className={[props.className, "as-searchForm-wrapper"].join(" ")}
              style={{
                ...props.style
              }}
            >
              {items}
              {submitter}
            </div>
            {/* <Selected /> */}
            <CollpaseLine fieldid={fieldid}/>
          </>
        );
      }
    },
    [collapsed, responColState]
  );

  const SearchFormValue = useMemo(() => {
    return {
      ...extendRef,
      setResponCol,
      collapsedConfig: {
        collapsed,
        collapsedLinesNumber,
        responCol,
        showCollapsed,
        collapsedNailed
      }
    };
  }, [extendRef, collapsed, collapsedNailed, collapsedLinesNumber, responCol]);

  // onSearch和onSubmit使用同一个方法
  const searchFun = restProps.onSearch || restProps.onSubmit;
  restProps.onSubmit = restProps.onSearch = searchFun;
  return (
    <SearchFormContext.Provider value={SearchFormValue}>
      <DataForm
        className="as-searchForm"
        style={{
          paddingBottom: showCollapsed ? "10px" : 0
        }}
        onKeyPress={(event) => {
          if (event.key === "Enter") {
            // formRef.current?.submit();
            let pValues, pErrors;
            return new Promise((resolve, reject) => {
              extendRef
                .validateFields()
                .then((values) => {
                  pValues = values;
                  resolve(values);
                })
                .catch((err) => {
                  pErrors = err;
                  reject(err);
                })
                .finally(() => {
                  props.onSearch?.(pValues, pErrors, formRef);
                });
            });
          }
        }}
        onFieldsChange={(props, changed) => {
          // console.log("FieldsChange-->", props, changed);
        }}
        fieldid={fieldid}
        {...restProps}
        formRef={extendRef}
        submitter={submitterProps}
        form={form || formInstance}
        contentRender={contentRender}
      ></DataForm>
    </SearchFormContext.Provider>
  );
};

export default React.forwardRef(SearchForm);
